import { useEffect, useRef } from "react";

import { useEscapeScope } from "./escape-scope";
import { tabStops, wrapTarget } from "./tab-ring";

/**
 * Shut a popover on anything that means the attorney has moved on.
 *
 * Escape hands focus back to the control that opened it, because a panel that vanishes
 * leaving focus on the document body loses whoever was using the keyboard. It takes the
 * key as the nearest scope, so a placement behind the panel keeps running. A press outside
 * counts rather than a click, so a press that begins outside and drags across the panel
 * still closes it.
 *
 * Focus reaching anything behind it counts too. Tab is not held inside the panel, and a
 * panel that stayed open while the controls underneath were being used would be a dialog
 * over its own page. Closing on the way out is the popover's answer to that, unless it is
 * contained, as below; trapping Tab with the page made inert is the modal's.
 *
 * A held popover, for work inside it that must be seen to the end such as a send in
 * flight, keeps Escape so nothing behind it answers the key, but the key neither closes it
 * nor moves focus: sending focus to the trigger would take the keyboard out of a panel
 * that is still open. Its `close` refuses while held, and the press and focus paths go
 * through it.
 *
 * A contained popover keeps the keyboard instead of closing when it leaves, for words the
 * attorney has not finished with, such as a form. Tab past the panel's last control goes
 * round to its first, and Shift+Tab past its first goes to its last. The trigger is not
 * one of the stops, because pressing it would close the form. Focus reaching the
 * page any other way is brought back to where it was. A press outside and Escape still
 * close it, as they close any popover.
 *
 * A scrim closes its popover on its own click: closed on the press, it would be gone before
 * the release, and the click would land on the control it was laid there to guard.
 */
export function useDismissed(
  open: boolean,
  panel: React.RefObject<HTMLElement | null>,
  trigger: React.RefObject<HTMLElement | null>,
  close: () => void,
  {
    held = false,
    contained = false,
    scrim,
  }: {
    held?: boolean;
    contained?: boolean;
    scrim?: React.RefObject<HTMLElement | null>;
  } = {},
) {
  // Read through a ref, so a caller's new `close` or a form opening does not
  // take the listeners off the document and put them back.
  const latestRef = useRef({ close, contained });
  useEffect(() => {
    latestRef.current = { close, contained };
  });

  useEffect(() => {
    if (!open) return undefined;
    const inside = (target: EventTarget | null) =>
      target instanceof Node &&
      [panel, trigger, scrim].some(
        (ref) => ref?.current?.contains(target) === true,
      );
    function pressed(event: MouseEvent) {
      if (!inside(event.target)) latestRef.current.close();
    }
    function focused(event: FocusEvent) {
      if (inside(event.target)) return;
      if (!latestRef.current.contained) {
        latestRef.current.close();
        return;
      }
      const left = event.relatedTarget;
      const back =
        left instanceof HTMLElement && inside(left)
          ? left
          : tabStops(panel.current).at(0);
      back?.focus();
    }
    document.addEventListener("mousedown", pressed);
    document.addEventListener("focusin", focused);
    return () => {
      document.removeEventListener("mousedown", pressed);
      document.removeEventListener("focusin", focused);
    };
  }, [open, panel, trigger, scrim]);

  // Tab is caught only while the popover holds words; a plain one lets it go.
  useEffect(() => {
    if (!open || !contained) return undefined;
    function tabbed(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const stops = tabStops(panel.current);
      const at = document.activeElement;
      const to =
        event.shiftKey && at === trigger.current
          ? stops.at(-1)
          : wrapTarget(stops, at, event.shiftKey);
      if (!to) return;
      event.preventDefault();
      to.focus();
    }
    document.addEventListener("keydown", tabbed);
    return () => {
      document.removeEventListener("keydown", tabbed);
    };
  }, [open, contained, panel, trigger]);

  useEscapeScope("popover", open, () => {
    if (held) return;
    close();
    trigger.current?.focus();
  });
}
